/**
 * ReportLenses - Lens analysis section with three parts:
 * 1. Per-lens cards with counts + AI summaries (grouped by lens)
 * 2. Pivot × lens heatmap matrix
 * 3. Human pivot point details (description + AI summaries + expandable items)
 */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, ScanSearch, X, MinusCircle, PlusCircle } from 'lucide-react';
import { Section, SectionTitle, SectionSubtitle, Paragraph, Card, SITRA } from './reportStyles';
import ModalWrapper from '../ModalWrapper';
import EditableText from './EditableText';
import TranslatedText from './TranslatedText';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { lensDisplayName } from '../../utils/lensDisplay';

// ─── Lens Grid ───────────────────────────────────────────────────────────────

const LensGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 24px 0;
  width: 100%;
`;

const LensCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  padding: 20px;
  flex: 1 1 260px;
  min-width: 0;
  max-width: 100%;
  border-top: 4px solid ${props => props.$color || '#6b7280'};
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.$color || '#6b7280'};
  }
`;

const LensName = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0 0 4px 0;
`;

const LensDescription = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const CountRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const CountBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 600;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

const MiniBar = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: #f3f4f6;
`;

const MiniBarSegment = styled.div`
  height: 100%;
  background: ${props => props.$color};
  width: ${props => props.$width}%;
`;

const ExpandBtn = styled.button`
  background: none;
  border: none;
  color: #367ABA;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  padding: 8px 0 0 0;

  &:hover { color: #2c5e91; }
`;

const ItemList = styled.div`
  margin-top: 8px;
  font-size: 13px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  color: #444;
`;

/* Replaced ItemDot with an icon — keeping the styled wrapper for layout
 * (the icon needs the same flex-aligned 5px top offset and color). */
const ItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${props => props.$color};
  flex-shrink: 0;
  margin-top: 3px;
`;

/* Section subtitle inside the lens modal — separates Esteet from
 * Mahdollisuudet so colour isn't the only carrier of meaning. */
const ItemGroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 16px 0 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.$color || '#374151'};
`;

// ─── AI Summary (shared) ─────────────────────────────────────────────────────

const SourceTag = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: 8px;
  vertical-align: middle;
  color: ${p => p.$manual ? '#7a3a0c' : '#7c3aed'};
  background: ${p => p.$manual ? 'rgba(255, 217, 61, 0.25)' : '#ede9fe'};
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 12px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: ${p => p.$type === 'obstacle' ? 'rgba(232, 17, 75, 0.08)' : '#f0fdf4'};
  border: 1px solid ${p => p.$type === 'obstacle' ? '#fecaca' : '#bbf7d0'};
  border-radius: 2px;
  padding: 12px 14px;
`;

const SummaryCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$type === 'obstacle' ? '#a83d10' : '#166534'};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SummaryCardText = styled.div`
  font-size: 13px;
  color: #4b5563;
  line-height: 1.5;
  font-style: italic;
`;

// ─── Lens Modal ──────────────────────────────────────────────────────────────

// Card uses flex column + overflow:hidden so the inner ModalBody owns the
// scroll. That keeps ModalHeader pinned at the top while the obstacles /
// opportunities list scrolls underneath — the close X stays reachable.
const ModalBox = styled.div`
  background: #fff;
  border-radius: 0;
  max-width: 680px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

// Tab strip sits between the (sticky) header and the scrolling body, so it
// stays pinned while the active tab's content scrolls underneath.
const ModalTabs = styled.div`
  display: flex;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
`;

const ModalTab = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: ${p => (p.$active ? '#fff' : '#fafafa')};
  border: none;
  border-bottom: 3px solid ${p => (p.$active ? '#000' : 'transparent')};
  color: ${p => (p.$active ? '#000' : '#6b7280')};
  font-size: 14px;
  font-weight: ${p => (p.$active ? 700 : 500)};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { color: #000; }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin: 0;
`;

const ModalCloseBtn = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  &:hover { background: #e5e7eb; color: #111827; }
`;


const ModalBody = styled.div`
  padding: 16px 24px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 20px;
`;

const ModalSummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.$type === 'obstacle' ? '#a83d10' : '#166534'};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModalSummaryText = styled.div`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 16px;
`;

const ModalSummaryCard = styled.div`
  background: ${p => p.$type === 'obstacle' ? 'rgba(232, 17, 75, 0.08)' : '#f0fdf4'};
  border: 1px solid ${p => p.$type === 'obstacle' ? '#fecaca' : '#bbf7d0'};
  border-radius: 0;
  padding: 16px;
  margin-bottom: 12px;
`;

const ModalCountRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

// ─── Heatmap ─────────────────────────────────────────────────────────────────

const HeatmapContainer = styled.div`
  overflow-x: auto;
  margin: 24px 0;
`;

const HeatmapTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const HeatmapTh = styled.th`
  padding: 8px 12px;
  text-align: center;
  font-weight: 600;
  color: #000;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
  font-size: 12px;
`;

const HeatmapTd = styled.td`
  padding: 8px 12px;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
`;

// Sitra brand: orange / yellow / green semantic — avoids the red+green clash
// the brand book explicitly flags as a color-blindness anti-pattern.
const HeatmapCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 28px;
  border-radius: 2px;
  font-weight: 700;
  font-size: 12px;
  color: ${props => {
    if (props.$total === 0) return '#999';
    const score = props.$opportunities - props.$obstacles;
    if (score === 0) return '#000';   // yellow needs black text for contrast
    return '#fff';
  }};
  background: ${props => {
    if (props.$total === 0) return '#f9fafb';
    const score = props.$opportunities - props.$obstacles;
    if (score > 0) return '#00613d';   // Sitra dark green — opportunities lead
    if (score < 0) return SITRA.puolukka;   // Sitra orange — obstacles lead
    return '#FFD93D';                  // Sitra yellow — balanced (was orange, clashed)
  }};
`;

const PivotLabel = styled.td`
  padding: 10px 12px;
  text-align: left;
  font-weight: 500;
  color: #000;
  border-bottom: 1px solid #f3f4f6;
  /* Allow full title to wrap — never ellipsis-truncate; titles ARE the data */
  max-width: 280px;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
  vertical-align: middle;
`;

const FooterCell = styled.td`
  padding: 8px 12px;
  text-align: center;
  font-weight: 700;
  border-top: 2px solid #e5e7eb;
  color: ${props => {
    if (props.$score > 0) return '#00613d';
    if (props.$score < 0) return SITRA.puolukka;
    return '#666';
  }};
`;

// ─── Heatmap Summary ─────────────────────────────────────────────────────────

const HeatmapSummary = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin: 16px 0 24px;

  @media (min-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ExplainerCard = styled.div`
  grid-column: 1 / -1;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  padding: 14px 18px;
  font-size: 13px;
  line-height: 1.6;
  color: #4b5563;
`;

const RankCard = styled.div`
  background: ${p => p.$positive ? '#f0fdf4' : 'rgba(232, 17, 75, 0.08)'};
  border: 1px solid ${p => p.$positive ? '#bbf7d0' : '#fecaca'};
  border-left: 4px solid ${p => p.$positive ? '#00613d' : SITRA.puolukka};
  border-radius: 0;
  padding: 14px 18px;
`;

const RankLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${p => p.$positive ? '#166534' : '#a83d10'};
  margin-bottom: 6px;
`;

const RankHeadline = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #000;
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const RankScore = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$positive ? '#00613d' : SITRA.puolukka};
`;

const RankBody = styled.div`
  font-size: 13px;
  color: #4b5563;
  line-height: 1.55;
`;

const RankList = styled.ul`
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  li { margin-bottom: 3px; }
`;

// ─── Human Pivot Cards ───────────────────────────────────────────────────────

const HumanPivotCard = styled(Card)`
  border-left: 3px solid #fa6129;
`;

const HumanPivotTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin-bottom: 6px;
`;

const HumanPivotDescription = styled.div`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ItemBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  color: #fff;
  background: ${props => props.$type === 'obstacle' ? SITRA.puolukka : '#00613d'};
`;

const PivotItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  color: #555;
  font-size: 13px;
`;

// The guard lives outside the body so the body's hooks always run in the same order.
export default function ReportLenses(props) {
  if (!props.lenses?.length || !props.lensItems?.length) return null;
  return <ReportLensesBody {...props} />;
}

function ReportLensesBody({ lenses, lensItems, pivotPoints, pivotEdits, printMode = false, summaryText }) {
  const { t, language } = useTranslation();
  const [selectedLens, setSelectedLens] = useState(null);
  const [lensTab, setLensTab] = useState('summary'); // 'summary' | 'items'
  const [expandedPivot, setExpandedPivot] = useState(null);
  const [showAllPivots, setShowAllPivots] = useState(printMode);

  // Build edits lookup
  const editsLookup = useMemo(() => {
    const lookup = {};
    (pivotEdits || []).forEach(e => { lookup[e.pivot_point_id] = e; });
    return lookup;
  }, [pivotEdits]);

  // Count items per lens
  const lensStats = useMemo(() => {
    const stats = {};
    lenses.forEach(lens => {
      const items = lensItems.filter(i => i.lens_id === lens.id);
      stats[lens.id] = {
        obstacles: items.filter(i => i.type === 'obstacle').length,
        opportunities: items.filter(i => i.type === 'opportunity').length,
        items,
      };
    });
    return stats;
  }, [lenses, lensItems]);

  // Heatmap: pivot x lens matrix
  const { matrixData, pivotGroups } = useMemo(() => {
    if (!pivotPoints?.length) return { matrixData: {}, pivotGroups: [] };

    const matrix = {};
    const groups = [];

    pivotPoints.forEach(pivot => {
      const pivotId = pivot.id;
      matrix[pivotId] = {};
      let totalObstacles = 0;
      let totalOpportunities = 0;

      lenses.forEach(lens => {
        const cellItems = lensItems.filter(
          i => i.pivot_point_id === pivotId && i.lens_id === lens.id
        );
        const obs = cellItems.filter(i => i.type === 'obstacle').length;
        const opp = cellItems.filter(i => i.type === 'opportunity').length;
        matrix[pivotId][lens.id] = { obstacles: obs, opportunities: opp };
        totalObstacles += obs;
        totalOpportunities += opp;
      });

      if (totalObstacles + totalOpportunities > 0) {
        const edit = editsLookup[pivotId];
        groups.push({
          pivotId,
          title: edit?.title || pivot.text || pivot.title || 'Vipupiste',
          obstacles: totalObstacles,
          opportunities: totalOpportunities,
        });
      }
    });

    groups.sort((a, b) => (b.obstacles + b.opportunities) - (a.obstacles + a.opportunities));
    return { matrixData: matrix, pivotGroups: groups };
  }, [pivotPoints, lenses, lensItems, editsLookup]);

  // Column totals (net score per lens across all pivots)
  const colScores = lenses.map(lens => {
    let obs = 0, opp = 0;
    Object.values(matrixData).forEach(row => {
      const cell = row[lens.id];
      if (cell) { obs += cell.obstacles; opp += cell.opportunities; }
    });
    return opp - obs;
  });

  // Ranked lenses for summary: most positive, most negative
  const lensRanking = useMemo(() => {
    const ranked = lenses.map((lens, i) => {
      const items = lensItems.filter(it => it.lens_id === lens.id);
      return {
        lens,
        netScore: colScores[i] ?? 0,
        obstacles: items.filter(it => it.type === 'obstacle'),
        opportunities: items.filter(it => it.type === 'opportunity'),
      };
    }).filter(r => r.obstacles.length + r.opportunities.length > 0);

    const mostPositive = [...ranked].sort((a, b) => b.netScore - a.netScore)[0] || null;
    const mostNegative = [...ranked].sort((a, b) => a.netScore - b.netScore)[0] || null;
    // Only surface the pair when they're actually distinct AND differ in sign/strength
    const distinct = mostPositive && mostNegative && mostPositive.lens.id !== mostNegative.lens.id;
    return { mostPositive: distinct ? mostPositive : null, mostNegative: distinct ? mostNegative : null };
  }, [lenses, lensItems, colScores]);

  // Human-created pivot points with details
  const humanPivots = useMemo(() => {
    if (!pivotPoints?.length) return [];
    return pivotPoints
      .filter(p => p.source === 'manual')
      .map(p => {
        const edit = editsLookup[p.id];
        return {
          ...p,
          displayTitle: edit?.title || p.text || p.title,
          displayDescription: edit?.description || p.description,
        };
      });
  }, [pivotPoints, editsLookup]);

  // Items by pivot for expandable list
  const itemsByPivot = useMemo(() => {
    const lookup = {};
    (lensItems || []).forEach(item => {
      if (!item.pivot_point_id) return;
      if (!lookup[item.pivot_point_id]) lookup[item.pivot_point_id] = [];
      lookup[item.pivot_point_id].push(item);
    });
    return lookup;
  }, [lensItems]);

  return (
    <Section id="lenses">
      <SectionTitle>{t('report.lenses.title')}</SectionTitle>
      <Paragraph>
        {summaryText || t('report.lenses.introDefault', { lensCount: lenses.length })}
      </Paragraph>

      {/* ─── Lens grid cards with AI summaries ─── */}
      <LensGrid>
        {lenses.map(lens => {
          const stat = lensStats[lens.id] || { obstacles: 0, opportunities: 0, items: [] };
          const total = stat.obstacles + stat.opportunities;

          return (
            <LensCard key={lens.id} $color={lens.color || '#6b7280'} onClick={() => { setSelectedLens(lens); setLensTab('summary'); }}>
              <LensName>{lensDisplayName(lens, language)}</LensName>
              {lens.description && (
                <LensDescription>
                  <EditableText fieldKey={`lens.${lens.id}.description`} fallback={lens.description} />
                </LensDescription>
              )}

              <CountRow>
                <CountBadge $bg={SITRA.puolukkaBg} $color={SITRA.puolukka}>
                  {stat.obstacles} {t('report.lenses.obstacles')}
                </CountBadge>
                <CountBadge $bg="#ecfdf5" $color="#00613d">
                  {stat.opportunities} {t('report.lenses.opportunityShort')}
                </CountBadge>
              </CountRow>

              {total > 0 && (
                <MiniBar>
                  <MiniBarSegment $color={SITRA.puolukka} $width={(stat.obstacles / total) * 100} />
                  <MiniBarSegment $color="#00613d" $width={(stat.opportunities / total) * 100} />
                </MiniBar>
              )}
            </LensCard>
          );
        })}
      </LensGrid>

      {/* Heatmap + nettopistemäärä poistettu käsikirjoitus 2026-05 mukaan
          ("Poistetaan koko Heatmap-kuva. ... nettopistemäärän voisi poistaa, on vähän hankala ymmärtää"). */}

      {/* ─── Human pivot point details ─── */}
      {humanPivots.length > 0 && (
        <>
          <SectionSubtitle style={{ marginTop: '40px' }}>
            {t('report.lenses.title')}
            <SourceTag $manual>{t('report.highlights.participant')}</SourceTag>
          </SectionSubtitle>
          <Paragraph>
            {t('report.lenses.humanIntro')}
          </Paragraph>

          {humanPivots.slice(0, showAllPivots ? undefined : 3).map(pivot => {
            const pivotItems = itemsByPivot[pivot.id] || [];
            const isExpanded = printMode || expandedPivot === pivot.id;
            const hasObstacleSummary = pivot.lensSummaryObstacles;
            const hasOpportunitySummary = pivot.lensSummaryOpportunities;

            return (
              <HumanPivotCard key={pivot.id}>
                <HumanPivotTitle>
                  {pivot.id
                    ? (
                      <EditableText
                        fieldKey={`pivot.${pivot.id}.title`}
                        fallback={pivot.displayTitle}
                        multiline={false}
                        label="Vipupisteen otsikko"
                      />
                    )
                    : pivot.displayTitle}
                </HumanPivotTitle>
                {pivot.displayDescription && (
                  <HumanPivotDescription>
                    <SourceTag>AI</SourceTag>{' '}
                    {pivot.id
                      ? (
                        <EditableText
                          fieldKey={`pivot.${pivot.id}.description`}
                          fallback={pivot.displayDescription}
                          label="Vipupisteen kuvaus"
                        />
                      )
                      : pivot.displayDescription}
                  </HumanPivotDescription>
                )}

                {(hasObstacleSummary || hasOpportunitySummary) && (
                  <SummaryRow>
                    {hasObstacleSummary && (
                      <SummaryCard $type="obstacle">
                        <SummaryCardTitle $type="obstacle">{t('report.lenses.obstacles')} <SourceTag>AI</SourceTag></SummaryCardTitle>
                        <SummaryCardText>
                          {pivot.id
                            ? (
                              <EditableText
                                fieldKey={`pivot.${pivot.id}.lensSummaryObstacles`}
                                fallback={pivot.lensSummaryObstacles}
                                label="Esteiden yhteenveto"
                              />
                            )
                            : pivot.lensSummaryObstacles}
                        </SummaryCardText>
                      </SummaryCard>
                    )}
                    {hasOpportunitySummary && (
                      <SummaryCard $type="opportunity">
                        <SummaryCardTitle $type="opportunity">{t('report.lenses.opportunities')} <SourceTag>AI</SourceTag></SummaryCardTitle>
                        <SummaryCardText>
                          {pivot.id
                            ? (
                              <EditableText
                                fieldKey={`pivot.${pivot.id}.lensSummaryOpportunities`}
                                fallback={pivot.lensSummaryOpportunities}
                                label="Mahdollisuuksien yhteenveto"
                              />
                            )
                            : pivot.lensSummaryOpportunities}
                        </SummaryCardText>
                      </SummaryCard>
                    )}
                  </SummaryRow>
                )}

                {pivotItems.length > 0 && (
                  <>
                    <ExpandBtn onClick={() => setExpandedPivot(isExpanded ? null : pivot.id)}>
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {isExpanded ? t('report.lenses.expandLess') : t('report.lenses.itemsCount', { count: pivotItems.length })}
                    </ExpandBtn>

                    {isExpanded && (
                      <ItemList>
                        {pivotItems.map(item => (
                          <PivotItemRow key={item.id}>
                            <ItemBadge $type={item.type}>
                              {item.type === 'obstacle' ? t('report.lenses.obstacleShort') : t('report.lenses.opportunityShort')}
                            </ItemBadge>
                            <span>
                              <TranslatedText
                                fieldKey={`lensItem.${item.id}.title`}
                                fallback={item.title}
                                multiline={false}
                                label={item.type === 'obstacle' ? 'Esteen kuvaus' : 'Mahdollisuuden kuvaus'}
                              />
                            </span>
                          </PivotItemRow>
                        ))}
                      </ItemList>
                    )}
                  </>
                )}
              </HumanPivotCard>
            );
          })}
          {humanPivots.length > 3 && !showAllPivots && (
            <button
              onClick={() => setShowAllPivots(true)}
              style={{
                display: 'block', margin: '16px auto', padding: '8px 24px',
                background: 'none', border: '1px solid #d1d5db', borderRadius: 8,
                color: '#6b7280', fontSize: '0.9rem', cursor: 'pointer',
              }}
            >
              {t('report.lenses.expandMore', { count: humanPivots.length })}
            </button>
          )}
        </>
      )}
      {/* ─── Lens detail modal ─── */}
      {selectedLens && (() => {
        const stat = lensStats[selectedLens.id] || { obstacles: 0, opportunities: 0, items: [] };
        return (
          <ModalWrapper visible onClose={() => setSelectedLens(null)} zIndex={1000} opacity={0.5} blur={0} showCloseButton={false}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{selectedLens.name}</ModalTitle>
                <ModalCloseBtn onClick={() => setSelectedLens(null)} aria-label={t('report.signIn.closeAriaLabel')}>
                  <X size={18} />
                </ModalCloseBtn>
              </ModalHeader>
              <ModalTabs role="tablist">
                <ModalTab
                  type="button"
                  role="tab"
                  $active={lensTab === 'summary'}
                  aria-selected={lensTab === 'summary'}
                  onClick={() => setLensTab('summary')}
                >
                  {t('report.nav.stats')}
                </ModalTab>
                <ModalTab
                  type="button"
                  role="tab"
                  $active={lensTab === 'items'}
                  aria-selected={lensTab === 'items'}
                  onClick={() => setLensTab('items')}
                >
                  {t('report.lenses.allItemsTab', { count: stat.items.length })}
                </ModalTab>
              </ModalTabs>
              <ModalBody>
                {lensTab === 'summary' && (<>
                {selectedLens.description && (
                  <ModalDescription>
                    <EditableText
                      fieldKey={`lens.${selectedLens.id}.description`}
                      fallback={selectedLens.description}
                      label={`${selectedLens.name} — kuvaus`}
                    />
                  </ModalDescription>
                )}

                <ModalCountRow>
                  <CountBadge $bg={SITRA.puolukkaBg} $color={SITRA.puolukka}>
                    <MinusCircle size={13} strokeWidth={2.4} style={{ marginRight: 6, verticalAlign: -2 }} />
                    {t('report.lenses.obstaclesCount', { count: stat.obstacles })}
                  </CountBadge>
                  <CountBadge $bg="#ecfdf5" $color="#00613d">
                    <PlusCircle size={13} strokeWidth={2.4} style={{ marginRight: 6, verticalAlign: -2 }} />
                    {t('report.lenses.opportunitiesCount', { count: stat.opportunities })}
                  </CountBadge>
                </ModalCountRow>

                {selectedLens.summary_obstacles && (
                  <ModalSummaryCard $type="obstacle">
                    <ModalSummaryLabel $type="obstacle">{t('report.lenses.obstacles')} <SourceTag>AI</SourceTag></ModalSummaryLabel>
                    <ModalSummaryText>
                      <EditableText
                        fieldKey={`lens.${selectedLens.id}.summary_obstacles`}
                        fallback={selectedLens.summary_obstacles}
                        label={`${selectedLens.name} — esteiden yhteenveto`}
                      />
                    </ModalSummaryText>
                  </ModalSummaryCard>
                )}

                {selectedLens.summary_opportunities && (
                  <ModalSummaryCard $type="opportunity">
                    <ModalSummaryLabel $type="opportunity">{t('report.lenses.opportunities')} <SourceTag>AI</SourceTag></ModalSummaryLabel>
                    <ModalSummaryText>
                      <EditableText
                        fieldKey={`lens.${selectedLens.id}.summary_opportunities`}
                        fallback={selectedLens.summary_opportunities}
                        label={`${selectedLens.name} — mahdollisuuksien yhteenveto`}
                      />
                    </ModalSummaryText>
                  </ModalSummaryCard>
                )}

                {!selectedLens.summary_obstacles && !selectedLens.summary_opportunities && (
                  <ModalDescription style={{ fontStyle: 'italic', margin: 0 }}>
                    {t('report.lenses.noSummaryYet')}
                  </ModalDescription>
                )}
                </>)}

                {lensTab === 'items' && (stat.items.length > 0 ? (() => {
                  // Split items into obstacles / opportunities and render
                  // them under separate subtitles so meaning isn't carried
                  // by colour alone. Each item also gets a Lucide icon
                  // (MinusCircle for este, PlusCircle for mahdollisuus)
                  // so screen readers and colour-blind viewers can tell
                  // them apart without reading the row colour.
                  const obstacles = stat.items.filter(i => i.type === 'obstacle');
                  const opportunities = stat.items.filter(i => i.type === 'opportunity');
                  return (
                    <ItemList>
                      {obstacles.length > 0 && (
                        <>
                          <ItemGroupTitle $color={SITRA.puolukka}>
                            <MinusCircle size={14} strokeWidth={2.4} />
                            {t('report.lenses.obstaclesWithCount', { count: obstacles.length })}
                          </ItemGroupTitle>
                          {obstacles.map(item => (
                            <ItemRow key={item.id}>
                              <ItemIcon $color={SITRA.puolukka} aria-label={t('report.lenses.obstacleAria')}>
                                <MinusCircle size={14} strokeWidth={2.4} />
                              </ItemIcon>
                              <span>
                                <TranslatedText
                                  fieldKey={`lensItem.${item.id}.title`}
                                  fallback={item.title}
                                  multiline={false}
                                  label="Esteen kuvaus"
                                />
                              </span>
                            </ItemRow>
                          ))}
                        </>
                      )}
                      {opportunities.length > 0 && (
                        <>
                          <ItemGroupTitle $color="#00613d">
                            <PlusCircle size={14} strokeWidth={2.4} />
                            {t('report.lenses.opportunitiesWithCount', { count: opportunities.length })}
                          </ItemGroupTitle>
                          {opportunities.map(item => (
                            <ItemRow key={item.id}>
                              <ItemIcon $color="#00613d" aria-label={t('report.lenses.opportunityAria')}>
                                <PlusCircle size={14} strokeWidth={2.4} />
                              </ItemIcon>
                              <span>
                                <TranslatedText
                                  fieldKey={`lensItem.${item.id}.title`}
                                  fallback={item.title}
                                  multiline={false}
                                  label="Mahdollisuuden kuvaus"
                                />
                              </span>
                            </ItemRow>
                          ))}
                        </>
                      )}
                    </ItemList>
                  );
                })() : (
                  <ModalDescription style={{ fontStyle: 'italic', margin: 0 }}>
                    {t('report.lenses.noItemsYet')}
                  </ModalDescription>
                ))}
              </ModalBody>
            </ModalBox>
          </ModalWrapper>
        );
      })()}
    </Section>
  );
}
